/**
 * @fileoverview Hook for unified job timeline combining system and user milestones
 * 
 * Fetches and combines system-generated milestones with user-created milestones,
 * groups them by phase, and calculates overall progress.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Milestone, MilestonePhase, SYSTEM_MILESTONE_CONFIG } from '@/lib/types/milestone';

interface UnifiedMilestonesResult {
  allMilestones: Milestone[];
  systemMilestones: Milestone[];
  userMilestones: Milestone[];
  milestonesByPhase: Record<MilestonePhase, Milestone[]>;
  currentUserMilestone?: Milestone;
  upcomingUserMilestones: Milestone[];
  completedCount: number;
  totalCount: number;
  progressPercentage: number;
  isLoading: boolean;
  error: Error | null;
}

export function useUnifiedMilestones(jobId: string): UnifiedMilestonesResult {
  const { data, isLoading, error } = useQuery({
    queryKey: ['unified-milestones', jobId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('milestones')
        .select('*')
        .eq('job_id', jobId)
        .order('milestone_order', { ascending: true });

      if (error) throw error;
      return data as Milestone[];
    },
    enabled: !!jobId,
  });

  const milestones = data || [];

  // Separate system and user milestones
  const systemMilestones = milestones.filter(m => m.is_system_milestone);
  const userMilestones = milestones
    .filter(m => !m.is_system_milestone && !m.is_blank)
    .sort((a, b) => {
      // Sort by due_date ascending (earliest first)
      // Handle null/undefined due_dates by putting them at the end
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });

  // Group milestones by phase
  const milestonesByPhase: Record<MilestonePhase, Milestone[]> = {
    setup: [],
    award: [],
    project: userMilestones,
    completion: [],
  };

  systemMilestones.forEach(milestone => {
    if (milestone.system_milestone_type) {
      const config = SYSTEM_MILESTONE_CONFIG[milestone.system_milestone_type];
      if (config) {
        milestonesByPhase[config.phase].push(milestone);
      }
    }
  });

  // Find current and upcoming user milestones
  const currentUserMilestone = userMilestones.find(m => !m.is_completed);
  const upcomingUserMilestones = userMilestones
    .filter(m => !m.is_completed)
    .slice(1, 4); // Show next 3 upcoming

  // Calculate progress
  const completedCount = milestones.filter(m => m.is_completed).length;
  const totalCount = milestones.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return {
    allMilestones: milestones,
    systemMilestones,
    userMilestones,
    milestonesByPhase,
    currentUserMilestone,
    upcomingUserMilestones,
    completedCount,
    totalCount,
    progressPercentage,
    isLoading,
    error: error as Error | null,
  };
}
